import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  getAllPlacesByAddress,
  getPlaceDetailById,
  PlacePrediction,
} from "../utils/getPlaceByAddress";
import { searchHospitalClinic } from "../services/StakeholderApi";

interface ApiItem {
  providerName?: string;
  providerAddress?: string;
  districtName?: string;
  physicalCity?: string;
  areaName?: string;
  localeName?: string;
  physicalState?: string;
  physicalStateCode?: string;
  physicalZip?: string;
  countryName?: string;
  country?: string;
  medicareId?: string;
  county?: string;
  latitude?: number | string | null;
  longitude?: number | string | null;
}

interface DisplayItem {
  id: string;
  source: "api" | "google";
  mainText: string;
  secondaryText: string;
  raw: ApiItem | PlacePrediction;
}

export interface LocationSuggestion {
  providerName: string;
  providerAddress: string;
  physicalCity: string;
  physicalState: string;
  physicalStateCode: string;
  physicalZip: string;
  country: string;
  countryCode: string;
  county: string;
  areaName: string;
  medicareId: string;
  latitude: number | null;
  longitude: number | null;
  source: "api" | "google";
}

export interface SearchHospitalAutosuggestProps {
  value: string;
  onChange: (value: string) => void;
  onSuggestionSelect: (suggestion: LocationSuggestion) => void;
  onEnterNewLocation?: () => void;
  showEnterNewLocation?: boolean;
  placeholder?: string;
  label?: string;
  required?: boolean;
  maxLength?: number;
  disabled?: boolean;
  minSearchLength?: number;
  searchApi?: (query: string) => Promise<any>;
}

const SearchHospitalAutosuggest: React.FC<SearchHospitalAutosuggestProps> = ({
  value,
  onChange,
  onSuggestionSelect,
  onEnterNewLocation,
  showEnterNewLocation = false,
  placeholder = "Search location...",
  label = "Location",
  required = false,
  maxLength = 200,
  disabled = false,
  minSearchLength = 3,
  searchApi,
}) => {
  const [items, setItems] = useState<DisplayItem[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hasSearched, setHasSearched] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const search = useCallback(
    debounce(async (query: string) => {
      if (!query.trim() || query.length < minSearchLength) {
        setItems([]);
        setShowSuggestions(false);
        setSelectedIndex(-1);
        setHasSearched(false);
        return;
      }

      try {
        setIsSearching(true);
        setSelectedIndex(-1);
        setHasSearched(true);

        // Step 1: Primary API (custom searchApi or default searchHospitalClinic)
        let apiResults: ApiItem[] = [];
        try {
          const apiFn = searchApi || searchHospitalClinic;
          const response: any = await apiFn(query);
          apiResults = Array.isArray(response?.result) ? response.result : [];
        } catch (_) {
          apiResults = [];
        }

        if (apiResults.length > 1) {
          setItems(
            apiResults.map((item, idx) => ({
              id: `api-${idx}`,
              source: "api" as const,
              mainText: item.providerName || item.localeName || item.areaName || "",
              secondaryText: item.providerAddress
                ? [item.providerAddress, item.physicalCity, item.physicalStateCode || item.physicalState, item.physicalZip].filter(Boolean).join(", ")
                : [item.areaName, item.districtName, item.physicalCity, item.physicalState, item.physicalZip].filter(Boolean).join(", "),
              raw: item,
            })),
          );
          setShowSuggestions(true);
          return;
        }

        // Step 2: Google Places fallback (also runs when apiResults has exactly 1 result)
        const googlePredictions = await getAllPlacesByAddress({
          address: query,
        });

        const apiItems = apiResults.map((item, idx) => ({
          id: `api-${idx}`,
          source: "api" as const,
          mainText: item.providerName || item.localeName || item.areaName || "",
          secondaryText: item.providerAddress
            ? [item.providerAddress, item.physicalCity, item.physicalStateCode || item.physicalState, item.physicalZip].filter(Boolean).join(", ")
            : [item.areaName, item.districtName, item.physicalCity, item.physicalState, item.physicalZip].filter(Boolean).join(", "),
          raw: item,
        }));

        const googleItems = googlePredictions.map((p, idx) => ({
          id: `google-${p.placeId || idx}`,
          source: "google" as const,
          mainText: p.mainText,
          secondaryText: p.secondaryText,
          raw: p,
        }));

        setItems([...apiItems, ...googleItems]);
        setShowSuggestions(true);
      } catch (error) {
        console.error("SearchHospitalAutosuggest search error:", error);
        setItems([]);
        setShowSuggestions(true);
      } finally {
        setIsSearching(false);
      }
    }, 500),
    [minSearchLength, searchApi],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    if (newValue.length >= minSearchLength) {
      search(newValue);
    } else {
      setItems([]);
      setShowSuggestions(false);
      setSelectedIndex(-1);
      setHasSearched(false);
    }
  };

  const handleSelectItem = useCallback(
    async (item: DisplayItem) => {
      onChange(item.mainText);
      setShowSuggestions(false);
      setItems([]);
      setSelectedIndex(-1);
      setHasSearched(false);

      if (item.source === "api") {
        const raw = item.raw as ApiItem;
        onChange(raw.providerName || [raw.localeName].filter(Boolean).join(", ") || item.mainText);
        onSuggestionSelect({
          providerName: raw.providerName || raw.localeName|| "",
          providerAddress: raw.providerAddress || [raw.areaName, raw.districtName].filter(Boolean).join(", ") || "",
          physicalCity: raw.physicalCity || raw.areaName || "",
          physicalState: raw.physicalStateCode || raw.physicalState || "",
          physicalStateCode: raw.physicalStateCode || "",
          physicalZip: raw.physicalZip || "",
          country: raw.countryName || raw.country || "",
          countryCode: "",
          county: raw.county || "",
          areaName: raw.areaName || "",
          medicareId: raw.medicareId || "",
          latitude: raw.latitude != null ? Number(raw.latitude) : null,
          longitude: raw.longitude != null ? Number(raw.longitude) : null,
          source: "api",
        });
      } else {
        const pred = item.raw as PlacePrediction;
        setIsFetchingDetails(true);
        try {
          const details = await getPlaceDetailById(pred.placeId, pred.mainText);
          if (details) {
            onSuggestionSelect({
              providerName: details.providerName,
              providerAddress: details.providerAddress,
              physicalCity: details.physicalCity,
              physicalState:
                details.physicalStateCode || details.physicalState,
              physicalStateCode: details.physicalStateCode,
              physicalZip: details.physicalZip,
              country: details.country,
              countryCode: details.countryCode,
              county: details.county,
              areaName: details.areaName || "",
              medicareId: "",
              latitude: details.latitude,
              longitude: details.longitude,
              source: "google",
            });
          }
        } finally {
          setIsFetchingDetails(false);
        }
      }
    },
    [onChange, onSuggestionSelect],
  );

  const handleEnterNewLocation = useCallback(() => {
    if (onEnterNewLocation) onEnterNewLocation();
    setShowSuggestions(false);
    setSelectedIndex(-1);
  }, [onEnterNewLocation]);

  const totalItems =
    items.length +
    (showEnterNewLocation && hasSearched && !isSearching ? 1 : 0);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions) return;
    if (totalItems === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
        break;

      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
        break;

      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0) {
          if (selectedIndex < items.length) {
            handleSelectItem(items[selectedIndex]);
          } else if (
            showEnterNewLocation &&
            selectedIndex === items.length
          ) {
            handleEnterNewLocation();
          }
        } else if (items.length > 0) {
          handleSelectItem(items[0]);
        }
        break;

      case "Escape":
        e.preventDefault();
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;

      case "Tab":
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (selectedIndex >= 0 && suggestionsRef.current) {
      const scrollable = suggestionsRef.current.querySelector(
        ".suggestions-scrollable",
      ) as HTMLElement;
      if (scrollable) {
        const el = scrollable.children[selectedIndex] as HTMLElement;
        if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [selectedIndex]);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [items]);

  const showEnterNew = showEnterNewLocation && hasSearched && !isSearching;
  const isLoading = isSearching || isFetchingDetails;

  return (
    <div className="form-floating searchable-dropdown position-relative">
      <input
        type="text"
        className="form-control"
        id={`floating-${label}`}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (value.length >= minSearchLength && items.length > 0) {
            setShowSuggestions(true);
          }
        }}
        placeholder={label}
        maxLength={maxLength}
        disabled={disabled || isFetchingDetails}
        ref={inputRef}
        autoComplete="off"
        role="combobox"
        aria-expanded={showSuggestions}
        aria-haspopup="listbox"
      />
      <label htmlFor={`floating-${label}`}>
        {label}
        {required && <span style={{ color: "red" }}>*</span>}
      </label>

      {showSuggestions && (
        <div
          ref={suggestionsRef}
          className="suggestion-dropdown position-absolute w-100 bg-white border mt-1 shadow-lg"
          style={{
            zIndex: 1000,
            borderRadius: "0.375rem",
            border: "1px solid #ced4da",
            top: "100%",
            left: 0,
            display: "flex",
            flexDirection: "column",
          }}
          role="listbox"
        >
          {isLoading ? (
            <div className="p-3 text-muted text-center">
              <div
                className="spinner-border spinner-border-sm me-2"
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              {isFetchingDetails
                ? "Loading address details..."
                : "Searching locations..."}
            </div>
          ) : (
            <>
              <div
                className="suggestions-scrollable"
                style={{ maxHeight: "250px", overflowY: "auto", flex: "1" }}
              >
                {items.length > 0 ? (
                  items.map((item, index) => (
                    <div
                      key={item.id}
                      className={`suggestion-item p-3 border-bottom ${
                        index === selectedIndex
                          ? "suggestion-item-selected"
                          : ""
                      }`}
                      onClick={() => handleSelectItem(item)}
                      role="option"
                      aria-selected={index === selectedIndex}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSelectItem(item);
                        }
                      }}
                      style={{
                        cursor: "pointer",
                        backgroundColor:
                          index === selectedIndex ? "#e7f1ff" : "white",
                        borderLeft:
                          index === selectedIndex
                            ? "3px solid #0d6efd"
                            : "3px solid transparent",
                        outline: "none",
                        transition: "all 0.15s ease-in-out",
                      }}
                      onMouseEnter={() => setSelectedIndex(index)}
                      onMouseLeave={() => setSelectedIndex(-1)}
                    >
                      <div className="fw-bold text-primary mb-1">
                        {item.mainText}
                      </div>
                      {item.secondaryText && (
                        <div className="text-dark small">
                          {item.secondaryText}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="p-3 text-muted text-center">
                    No locations found. Try a different search term.
                  </div>
                )}
              </div>

              {showEnterNew && (
                <div
                  className="manual-location-button border-top"
                  style={{ flexShrink: 0, borderTop: "2px solid #dee2e6" }}
                >
                  <div
                    className={`suggestion-item p-3 ${
                      selectedIndex === items.length
                        ? "suggestion-item-selected"
                        : ""
                    }`}
                    onClick={handleEnterNewLocation}
                    role="option"
                    aria-selected={selectedIndex === items.length}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleEnterNewLocation();
                      }
                    }}
                    style={{
                      cursor: "pointer",
                      backgroundColor:
                        selectedIndex === items.length ? "#e7f1ff" : "white",
                      borderLeft:
                        selectedIndex === items.length
                          ? "3px solid #0d6efd"
                          : "3px solid transparent",
                      outline: "none",
                      transition: "all 0.15s ease-in-out",
                    }}
                    onMouseEnter={() => setSelectedIndex(items.length)}
                    onMouseLeave={() => setSelectedIndex(-1)}
                  >
                    <div className="d-flex align-items-center justify-content-center text-primary">
                      <span className="material-icons mx-1 text-primary">
                        add
                      </span>
                      <span>Enter New Location</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate?: boolean,
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    const later = () => {
      timeout = null;
      if (!immediate) func.apply(null, args);
    };

    const callNow = immediate && !timeout;

    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);

    if (callNow) func.apply(null, args);
  };
}

export default SearchHospitalAutosuggest;
